import classNames from "classnames";
import React, { FC, useMemo } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";

import { pageCount, pageLabel, visiblePages } from "./pager.utils";

type Props = {
  /** Current page (1-based). */
  page: number;
  onChange: (page: number) => void;
  /** Rows per page and total rows: give both for "Showing 21–40 of 510". */
  pageSize?: number;
  total?: number;
  /** Used when the total isn't known (the label then says "Page 2 of 7"). */
  totalPages?: number;
  /** Ignore clicks (e.g. while the next page loads). */
  disabled?: boolean;
  /** Label when total is 0. */
  emptyLabel?: string;
  /** Extra classes for the bar (padding / border). */
  className?: string;
};

const ARROW =
  "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#E2E6EE] bg-white text-[#3A4256] hover:bg-[#F5F6F8] disabled:cursor-default disabled:text-[#C9CDD6] disabled:hover:bg-white";

/**
 * The page selection under a list, in the Work page's style (Roadmap 1867):
 * "Showing 21–40 of 510" on the left, ‹ 1 2 [3] 4 5 › on the right.
 */
export const ListPager: FC<Props> = ({
  page,
  onChange,
  pageSize,
  total,
  totalPages,
  disabled,
  emptyLabel,
  className,
}) => {
  const hasTotal = typeof total === "number" && !!pageSize;
  const pages = hasTotal
    ? pageCount(total as number, pageSize as number)
    : Math.max(1, totalPages || 1);
  const current = Math.min(Math.max(1, page), pages);
  const numbers = useMemo(() => visiblePages(current, pages), [current, pages]);
  // Without a total there is nothing useful to say on a single page.
  if (!hasTotal && pages <= 1) return null;
  const go = (next: number) => {
    if (disabled || next === current || next < 1 || next > pages) return;
    onChange(next);
  };

  return (
    <nav
      aria-label={"Pages"}
      className={classNames(
        "flex flex-wrap items-center justify-between gap-3 border-t border-[#F0F1F4] px-[22px] py-3.5",
        className,
      )}>
      <span className={"text-[13px] font-semibold text-[#8A90A2]"}>
        {hasTotal
          ? pageLabel(current, pageSize as number, total as number, emptyLabel)
          : `Page ${current} of ${pages}`}
      </span>
      {pages > 1 && (
        <div className={"flex items-center gap-1.5"}>
          <button
            type={"button"}
            aria-label={"Previous page"}
            className={ARROW}
            disabled={current <= 1}
            onClick={() => go(current - 1)}>
            <LuChevronLeft size={15} strokeWidth={2.4} />
          </button>
          {numbers.map((n) => (
            <button
              type={"button"}
              key={n}
              aria-label={`Page ${n}`}
              aria-current={n === current ? "page" : undefined}
              onClick={() => go(n)}
              className={classNames(
                "inline-flex h-8 min-w-[32px] items-center justify-center rounded-lg border px-1.5 text-[13px] font-bold",
                n === current
                  ? "border-[#6B4EFF] bg-[#6B4EFF] text-white"
                  : "border-[#E2E6EE] bg-white text-[#3A4256] hover:bg-[#F5F6F8]",
              )}>
              {n}
            </button>
          ))}
          <button
            type={"button"}
            aria-label={"Next page"}
            className={ARROW}
            disabled={current >= pages}
            onClick={() => go(current + 1)}>
            <LuChevronRight size={15} strokeWidth={2.4} />
          </button>
        </div>
      )}
    </nav>
  );
};
